// ============================================================
// LoginPage.js — die Eingangs-Tür der App
//
// Hier passiert der komplette Login-Flow — und der ist nicht
// einfach "E-Mail + Passwort rein, fertig". Weil Sicherheit.
//
// 3 Zustände die angezeigt werden können:
//
// 1. Normaler Login-Screen (E-Mail + Passwort)
//
// 2. 2FA-Verify-Screen — wenn User schon 2FA eingerichtet hat
//    → gibt Code aus Authenticator-App oder E-Mail ein
//
// 3. 2FA-Setup-Screen — beim ERSTEN Login MUSS 2FA eingerichtet
//    werden (entweder Authenticator-App mit QR-Code oder E-Mail)
//    → kein Login ohne 2FA möglich, no cap
//
// Flow:
//   Login → Backend prüft Passwort → gibt temp_token zurück
//   → 2FA-Code eingeben → Backend gibt echten Token → eingeloggt
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
// Eye/EyeOff = Passwort anzeigen/verstecken Toggle
// Shield = Icon für den 2FA-Screen
import { Eye, EyeOff, Shield, ArrowLeft } from 'lucide-react';

const ARIWA_LOGO = "/ariwa-animal-rights-watch-logo.png?v=20260227";

const LoginPage = () => {
  const navigate = useNavigate();
  // location.state?.from = wohin User wollte bevor er zum Login geleitet wurde
  const location = useLocation();
  // completeLogin = setzt Token + User nach erfolgreichem 2FA-Verify
  const { completeLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  // showPassword = ob das Passwort im Klartext angezeigt wird (Auge-Icon)
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // ---- 2FA States ----
  // needs2FA = User hat 2FA schon eingerichtet, braucht jetzt den Code
  const [needs2FA, setNeeds2FA] = useState(false);
  // needs2FASetup = User muss 2FA JETZT zum ersten Mal einrichten
  const [needs2FASetup, setNeeds2FASetup] = useState(false);
  // tempToken = kurzzeitiger Token der nur für den 2FA-Step gilt (läuft nach 10min ab)
  const [tempToken, setTempToken] = useState('');
  // twoFaMethod = 'totp' (Authenticator-App) oder 'email'
  const [twoFaMethod, setTwoFaMethod] = useState('totp');
  // twoFaCode = der 6-stellige Code den der User eingibt
  const [twoFaCode, setTwoFaCode] = useState('');
  // setupData = QR-Code + Secret vom Backend für TOTP-Einrichtung
  const [setupData, setSetupData] = useState(null);
  const [setupError, setSetupError] = useState('');

  // wenn User von einer anderen Seite zum Login umgeleitet wurde: nach Login dahin zurück
  const from = location.state?.from?.pathname || null;

  // load2FASetup — lädt QR-Code und Secret vom Backend für die Ersteinrichtung
  const load2FASetup = async (token, method) => {
    setLoading(true);
    setSetupError('');
    setSetupData(null);
    try {
      const setupResponse = await axios.post(`${API}/auth/2fa/bootstrap/setup`, {
        temp_token: token,
        method
      });
      setSetupData(setupResponse.data);
    } catch (error) {
      setSetupError(error.response?.data?.detail || '2FA-Setup konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  // handleLogin — Hauptfunktion: E-Mail + Passwort ans Backend schicken
  // Drei mögliche Antworten vom Backend:
  // a) requires_2fa_setup → erster Login, 2FA muss jetzt eingerichtet werden
  // b) requires_2fa → 2FA ist schon eingerichtet, Code eingeben
  // c) normaler Login → access_token direkt, direkt eingeloggt (theoretisch, aber nie passiert weil 2FA Pflicht)
  const handleLogin = async (e) => {
    e.preventDefault(); // verhindert Seiten-Reload beim Form-Submit
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });

      if (response.data.requires_2fa_setup) {
        // erster Login: 2FA-Setup-Screen anzeigen + QR-Code laden
        const setupMethod = response.data.method || 'totp';
        setNeeds2FASetup(true);
        setTempToken(response.data.temp_token);
        setTwoFaMethod(setupMethod);
        setSetupError('');
        setSetupData(null);
        await load2FASetup(response.data.temp_token, setupMethod);
        return;
      }

      if (response.data.requires_2fa) {
        // 2FA schon eingerichtet: Code-Eingabe-Screen anzeigen
        setNeeds2FA(true);
        setTempToken(response.data.temp_token);
        setTwoFaMethod(response.data.method);
        setLoading(false);
        return;
      }

      // direkt einloggen (passiert nur wenn 2FA irgendwie übersprungen wird)
      const { access_token, user } = response.data;
      completeLogin(access_token, user);
      toast.success(`Willkommen, ${user.name}!`);
      // zu der Seite navigieren wo User hinwollte, sonst zur Landing Page, sonst /contacts
      navigate(from || user.landing_page || '/contacts', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login fehlgeschlagen');
      setLoading(false);
    }
  };

  // handleVerify2FA — 2FA-Code verifizieren wenn 2FA schon eingerichtet ist
  // schickt Code + tempToken → Backend gibt echten access_token zurück
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFaCode.trim()) { toast.error('Bitte Code eingeben'); return; }
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        code: twoFaCode,
        temp_token: tempToken
      });

      const { access_token, user } = response.data;
      completeLogin(access_token, user);
      toast.success(`Willkommen, ${user.name}!`);
      navigate(from || user.landing_page || '/contacts', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiger Code');
      setLoading(false);
    }
  };

  // handleEnable2FASetup — 2FA zum ersten Mal aktivieren
  // Bei TOTP: QR-Code wurde gescannt, jetzt Code aus App eingeben zum Bestätigen
  // Bei E-Mail: direkt aktivieren, Code kommt per Mail
  const handleEnable2FASetup = async (e) => {
    e.preventDefault();
    if (twoFaMethod === 'totp' && !twoFaCode.trim()) { toast.error('Bitte Code eingeben'); return; }
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/2fa/bootstrap/enable`, {
        // bei TOTP: der Code aus der App; bei E-Mail: 'email' als Platzhalter
        code: twoFaMethod === 'totp' ? twoFaCode : 'email',
        temp_token: tempToken
      });

      if (response.data.requires_2fa) {
        // bei E-Mail-Methode: nach Aktivierung direkt zum Code-Verify-Screen
        setNeeds2FASetup(false);
        setNeeds2FA(true);
        setTempToken(response.data.temp_token || tempToken);
        setTwoFaMethod(response.data.method || 'email');
        setTwoFaCode('');
        setLoading(false);
        toast.success('Testcode wurde per E-Mail gesendet');
        return;
      }

      // Erfolg: 2FA eingerichtet und direkt eingeloggt
      const { access_token, user } = response.data;
      completeLogin(access_token, user);
      toast.success(`Willkommen, ${user.name}! 2FA wurde eingerichtet.`);
      navigate(from || user.landing_page || '/contacts', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || '2FA-Einrichtung fehlgeschlagen');
      setLoading(false);
    }
  };

  // handleBack — zurück zum normalen Login-Screen (alle 2FA-States resetten)
  const handleBack = () => {
    setNeeds2FA(false);
    setNeeds2FASetup(false);
    setTempToken('');
    setTwoFaCode('');
    setSetupData(null);
    setSetupError('');
  };

  // handleReload2FASetup — QR-Code neu laden wenn er nicht geladen hat
  const handleReload2FASetup = async () => {
    if (!tempToken) return;
    await load2FASetup(tempToken, twoFaMethod);
  };

  // handle2FAMethodChange — wenn User zwischen Authenticator-App und E-Mail wechselt
  // Setup-Daten neu laden weil QR-Code sich ändert
  const handle2FAMethodChange = async (value) => {
    setTwoFaMethod(value);
    setTwoFaCode('');
    if (!tempToken) return;
    await load2FASetup(tempToken, value);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <div className="w-full max-w-md">

        {/* Logo + App-Name */}
        <div className="flex items-center justify-center mb-8 gap-3">
          <div className="bg-white rounded-xl p-2">
            <img src={ARIWA_LOGO} alt="ARIWA Logo" className="h-12 w-auto" />
          </div>
          <div>
            <h1 className="text-3xl font-bold heading-font tracking-tight">Presseverteiler</h1>
          </div>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="pb-4 text-center">
            {/* Header wechselt je nach aktuellem Zustand (Login / 2FA-Code / 2FA-Setup) */}
            {needs2FA ? (
              <>
                <Shield className="h-8 w-8 mx-auto text-primary mb-2" />
                <h2 className="text-xl font-semibold">Zwei-Faktor-Authentifizierung</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {twoFaMethod === 'totp'
                    ? 'Gib den Code aus deiner Authenticator App ein'
                    : 'Ein Code wurde an deine E-Mail gesendet'}
                </p>
              </>
            ) : needs2FASetup ? (
              <>
                <Shield className="h-8 w-8 mx-auto text-primary mb-2" />
                <h2 className="text-xl font-semibold">2FA jetzt einrichten</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Beim ersten Login musst du die Zwei-Faktor-Authentifizierung aktivieren.
                </p>
              </>
            ) : (
              <h2 className="text-xl font-semibold">Anmelden</h2>
            )}
          </CardHeader>

          <CardContent>
            {/* ---- Zustand 1: 2FA-Code-Eingabe (wenn 2FA schon eingerichtet) ---- */}
            {needs2FA ? (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="2fa-code">
                    {twoFaMethod === 'totp' ? '6-stelliger Code' : 'E-Mail Code'}
                  </Label>
                  {/* tracking-widest = große Abstände zwischen Ziffern, sieht wie ein Code aus */}
                  <Input
                    id="2fa-code"
                    placeholder={twoFaMethod === 'totp' ? '000000' : 'Code eingeben'}
                    value={twoFaCode}
                    onChange={(e) => setTwoFaCode(e.target.value)}
                    maxLength={twoFaMethod === 'totp' ? 6 : 10}
                    className="text-center text-lg tracking-widest"
                    autoFocus
                    required
                    data-testid="2fa-code-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="verify-2fa-btn">
                  {loading ? 'Überprüfe...' : 'Bestätigen'}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Zurück zum Login
                </Button>
              </form>

            ) : needs2FASetup ? (
              /* ---- Zustand 2: 2FA-Ersteinrichtung ---- */
              <form onSubmit={handleEnable2FASetup} className="space-y-4">
                {setupError && <p className="text-sm text-destructive">{setupError}</p>}

                {/* Auswahl: Authenticator-App oder E-Mail */}
                <div className="space-y-2">
                  <Label>Methode</Label>
                  <Select value={twoFaMethod} onValueChange={handle2FAMethodChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="totp">Authenticator App</SelectItem>
                      <SelectItem value="email">E-Mail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* QR-Code anzeigen wenn TOTP gewählt und Setup-Daten geladen */}
                {twoFaMethod === 'totp' && setupData?.qr_code && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">1. Scanne den QR-Code:</p>
                    <div className="flex justify-center p-3 bg-white rounded-lg border">
                      <img src={setupData.qr_code} alt="2FA QR Code" className="w-44 h-44" />
                    </div>
                    {/* Manueller Key als Fallback wenn QR-Code-Scanner nicht funktioniert */}
                    <p className="text-xs text-muted-foreground">
                      Oder manuell eingeben: <code>{setupData.secret}</code>
                    </p>
                  </div>
                )}

                {/* Schritt 2: Code aus App eingeben um Einrichtung zu bestätigen */}
                {twoFaMethod === 'totp' ? (
                  <div className="space-y-2">
                    <Label htmlFor="setup-2fa-code">2. Code aus der App</Label>
                    <Input
                      id="setup-2fa-code"
                      placeholder="000000"
                      value={twoFaCode}
                      onChange={(e) => setTwoFaCode(e.target.value)}
                      maxLength={6}
                      className="text-center text-lg tracking-widest"
                      required
                      data-testid="setup-2fa-code-input"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Du erhälst deinen Code per E-Mail.
                  </p>
                )}

                <Button type="submit" className="w-full" disabled={loading} data-testid="setup-2fa-submit-btn">
                  {loading ? 'Aktiviere...' : '2FA aktivieren und anmelden'}
                </Button>
                {/* Neu laden wenn QR-Code nicht angezeigt wird */}
                {twoFaMethod === 'totp' && !setupData?.qr_code && (
                  <Button type="button" variant="outline" className="w-full" onClick={handleReload2FASetup} disabled={loading}>
                    Setup erneut laden
                  </Button>
                )}
                <Button type="button" variant="ghost" className="w-full" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Zurück zum Login
                </Button>
              </form>

            ) : (
              /* ---- Zustand 3: normaler Login-Screen ---- */
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-Mail</Label>
                  <Input id="login-email" type="email" placeholder="name@ariwa.org" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Passwort</Label>
                  <div className="relative">
                    {/* type wechselt zwischen 'password' und 'text' für Sichtbarkeit */}
                    <Input id="login-password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
                    {/* Auge-Icon um Passwort sichtbar zu machen */}
                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Wird angemeldet...' : 'Anmelden'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer mit Copyright */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          &copy; 2026 ARIWA - Animal Rights Watch e.V.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
